import React, { useState, useEffect, useMemo } from 'react';

import Select from 'react-select';

const toOption = (user) => ({ label: user.name ? user.name : `${user.first_name} ${user.last_name}`, value: user.id });

export default function UserSelectorInput({ selectedId, fetchUrl, onChange, usersList = [], fetchIfNoUsers = true, ghost = false, selectedUser, ...props }) {
    const [users, setUsers] = useState([]);

    const options = useMemo(() => {
        const mapped = users?.map(toOption) || [];

        // The lists this field draws on are paginated, so a selected user beyond the first page
        // has no option to match and the field renders as though nobody were selected. Callers
        // that already hold the selected user pass it in so it stays displayable.
        //
        // It has to BE the selected user: injecting an option for anyone else would leave the
        // field just as blank, having added a name the viewer never picked.
        if (selectedId && selectedUser?.id === selectedId && !mapped.some((option) => option.value === selectedId)) {
            return [...mapped, toOption(selectedUser)];
        }

        return mapped;
    }, [users, selectedId, selectedUser]);

    const value = useMemo(() => options.filter((option) => selectedId === option.value) || '', [selectedId, options]);

    useEffect(() => {
        if (usersList.length > 0) {
            setUsers(usersList);
        }
    }, [usersList]);

    useEffect(() => {
        let isMounted = true;
        if (fetchIfNoUsers && usersList.length === 0) {
            if (!fetchUrl) {
                // An empty list with no url to fill it from is a legitimate state:
                // the surrounding provider's users request is refused for a role
                // that cannot list users. Throwing took the whole surrounding
                // panel down and left a blank grey area where the form should be,
                // so render with no options instead.
                console.warn('UserSelectorInput: no users and no fetchUrl — rendering an empty list');

                return;
            }

            axios
                .get(fetchUrl)
                .then(({ data }) => {
                    if (isMounted) {
                        setUsers(data?.data || []);
                    }
                })
                .catch((error) => {
                    console.log(error);
                });
        }

        return () => {
            isMounted = false;
        };
    }, []);

    return (
        <Select
            className={ghost ? 'react-select-container--ghost' : 'react-select-container'}
            classNamePrefix="react-select"
            options={options}
            value={value}
            onChange={(selectedOption, { action }) => {
                if (action === 'clear') {
                    onChange(undefined);
                    return;
                }
                if (action === 'select-option') {
                    onChange(selectedOption.value);
                    return;
                }
            }}
            isDisabled={props.disabled}
            isClearable={props.clearable}
            menuPortalTarget={document.body}
            {...props}
            styles={{ ...props.styles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
        />
    );
}
